import { defaultRows } from "../../../api.app/controllers/constants";
import { SelectionType, ShapeForm, ShapeType } from "../components/process-design/body/sidebar/flow/Constants";
import {
  getCircle,
  getConnection,
  SPACE_BETWEEN_VLINES,
} from "../components/process-design/body/sidebar/flow/ElementUtils";
import { arrayMoveMutable, removeItems } from "../utils/array";
import { CustomCursorTypes, HotkeyScopes } from "../utils/constants";
import { produce } from "immer";

const initialState = {
  shapes: [],
  shapeMap: {},
  connections: {},
  connectionUniqueKey: 0,
  newArrow: {
    activeCursor: CustomCursorTypes.Default,
    startShapeId: null,
    startShapeY: null,
    endShapeId: null,
  },
  selection: {
    minPoint: null,
    maxPoint: null,
    id: null,
    type: SelectionType.Circle,
  },
  arrowDraggingId: null,
  cluePointsId: null,
  showVerticalLines: false,
  persistTimeout: null,
  version: null,
  flowContextMenu: null,
};

export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {
    SET_STATE(state, data) {
      state.shapes = [...data.shapes];
      state.shapeMap = { ...data.shapeMap };
      state.connections = { ...data.connections };
      state.connectionUniqueKey = data.connectionUniqueKey;
      state.showVerticalLines = data.showVerticalLines;
    },
    DELETE_SHAPE(state, activityId) {
      const shape = { ...state.shapeMap[`activity_${activityId}`] };

      //remove shape
      state.shapes = produce(state.shapes, (shapes) => {
        return shapes.filter((shapeId) => shapeId !== `activity_${activityId}`);
      });

      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        delete shapeMap[`activity_${activityId}`];
        state.shapes.forEach((shapeId) => {
          const newShape = shapeMap[shapeId];
          newShape.incommingConnections = removeItems(newShape.incommingConnections, shape.outgoingConnections);
          newShape.outgoingConnections = removeItems(newShape.outgoingConnections, shape.incommingConnections);
        });
      });

      state.connections = produce(state.connections, (connections) => {
        [...shape.incommingConnections, ...shape.outgoingConnections].forEach((id) => {
          delete connections[id];
        });
      });
    },
    APPEND_CONNECTION(state, connection) {
      state.connections = produce(state.connections, (connections) => {
        connections[connection.id] = connection;
      });
      state.connectionUniqueKey++;
    },
    REMOVE_CONNECTION(state, id) {
      state.connections = produce(state.connections, (connections) => {
        delete connections[id];
      });
    },
    REMOVE_CONNECTION_FROM_SHAPE(state, { shapeId, connectionId }) {
      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        shapeMap[shapeId].incommingConnections = shapeMap[shapeId].incommingConnections.filter(
          (conn) => conn !== connectionId
        );
        shapeMap[shapeId].outgoingConnections = shapeMap[shapeId].outgoingConnections.filter(
          (conn) => conn !== connectionId
        );
      });
    },
    ADD_SHAPE(state, { shape, index }) {
      state.shapes = produce(state.shapes, (shapes) => {
        shapes.splice(index, 0, shape.id);
      });
      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        shapeMap[shape.id] = shape;
      });
    },
    UPDATE_SHAPE(state, shape) {
      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        shapeMap[shape.id] = shape;
      });
    },
    UPDATE_CONNECTION(state, { id, points }) {
      state.connections = produce(state.connections, (connections) => {
        connections[id].attributes = [...points];
      });
    },
    SET_SHAPE_TYPE(state, { shapeType, shapeId }) {
      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        shapeMap[shapeId] = { ...shapeMap[shapeId], type: shapeType };
      });
    },
    SET_SELECTION(state, data) {
      state.selection = { ...data };
    },
  },
  actions: {
    async persist({ commit, dispatch, state }) {
      clearTimeout(state.persistTimeout);
      state.persistTimeout = setTimeout(async () => {
        try {
          const clonedState = {
            shapes: state.shapes,
            shapeMap: state.shapeMap,
            connections: state.connections,
            connectionUniqueKey: state.connectionUniqueKey,
            showVerticalLines: state.showVerticalLines,
            version: 0.1,
          };
          await dispatch("cells/updateCustomFlow", clonedState, { root: true });
        } catch (err) {
          console.error(err);
        }
      }, 1000);
    },
    async addShape({ commit, dispatch, state, rootState }, { id, atIndex, isNew }) {
      if (!atIndex) {
        atIndex = state.shapes.length;
      }
      // create default shape (circle)
      const newDefaultShape = {
        id: `activity_${id}`,
        form: ShapeForm.Default,
        type: ShapeType.Blank,
        attributes: getCircle(atIndex),
        incommingConnections: [],
        outgoingConnections: [],
      };

      commit("ADD_SHAPE", { shape: newDefaultShape, index: atIndex });

      // create a connection between New Shape and previous Shape if previous shape exists
      if (atIndex > 0) {
        const previousShapeId = state.shapes[atIndex - 1];
        await dispatch("addConnection", {
          startShapeId: previousShapeId,
          endShapeId: newDefaultShape.id,
        });
      }

      dispatch("calculateCys");
      await dispatch("persist");
    },
    async deleteShape({ commit, dispatch, state, rootState }, rowIds) {
      rowIds.forEach((rowId) => {
        if (state.shapeMap[`activity_${rowId}`]) commit("DELETE_SHAPE", rowId);
      });
      dispatch("calculateCys");
      await dispatch("persist");
    },
    // set state for new selected design
    async load({ commit, dispatch, state }, customFlow) {
      try {
        if (customFlow) commit("SET_STATE", JSON.parse(customFlow));
        else dispatch("resetState");
      } catch (error) {
        console.log(error);
      }
    },
    async updateConnection({ commit, dispatch, state }, { id, points }) {
      commit("UPDATE_CONNECTION", { id, points });
    },
    setSelectedShape({ commit, dispatch, state }, shapeId) {
      commit("SET_SELECTED_SHAPE", shapeId);
    },
    async setShapeType({ commit, dispatch, state, rootState }, shapeType) {
      const activeCellId = rootState.cells.activeCellId;
      const rowId = activeCellId.split("_")[0];
      if (state.selection.type === SelectionType.Arrow || defaultRows.some((x) => x.id === rowId)) return;

      const shapeId = state.selection.id || `activity_${rowId}`;
      const newShapeType = shapeType ? +shapeType : (state.shapeMap[shapeId].type + 1) % 4;

      commit("SET_SHAPE_TYPE", { shapeType: newShapeType, shapeId });
      await dispatch("persist");
    },
    async addConnection({ commit, dispatch, state }, { startShapeId, endShapeId }) {
      const startShape = state.shapeMap[startShapeId];
      const endShape = state.shapeMap[endShapeId];

      const newConnection = {
        id: state.connectionUniqueKey,
        attributes: getConnection(startShape, endShape),
        startShapeId: startShape.id,
        endShapeId: endShape.id,
      };

      commit("APPEND_CONNECTION", newConnection);

      startShape.outgoingConnections.push(newConnection.id);
      endShape.incommingConnections.push(newConnection.id);

      commit("UPDATE_SHAPE", startShape);
      commit("UPDATE_SHAPE", endShape);
    },
    setNewArrowActive({ commit, dispatch, state }) {
      state.newArrow.activeCursor = CustomCursorTypes.Start;
    },
    async setNewArrowData({ commit, dispatch, state }, { id, startShapeY }) {
      if (state.newArrow.activeCursor === CustomCursorTypes.Default) return;

      if (!state.newArrow.startShapeId) {
        state.newArrow.startShapeId = id;
        state.newArrow.startShapeY = startShapeY;
        state.newArrow.activeCursor = CustomCursorTypes.End;
        return;
      }

      if (id === state.newArrow.startShapeId) {
        await dispatch("resetSelection");
        return await dispatch("resetArrowData");
      }

      state.newArrow.endShapeId = id;
      await dispatch("addConnection", {
        startShapeId: state.newArrow.startShapeId,
        endShapeId: id,
      });
      await dispatch("persist");

      // Reset
      await dispatch("resetArrowData");
    },
    resetArrowData({ commit, dispatch, state }) {
      state.newArrow = {
        activeCursor: CustomCursorTypes.Default,
        startShapeId: null,
        endShapeId: null,
        startShapeY: null,
      };
    },
    async moveShape({ commit, dispatch, state }, { shapeId, cx }) {
      state.shapeMap = produce(state.shapeMap, (shapeMap) => {
        shapeMap[shapeId].attributes.cx = cx;
      });
    },
    // when activity moves, move shapes as well (change it's index)
    repositionShape({ commit, dispatch, state }, { sourceIndex, targetIndex, count }) {
      state.shapes = produce(state.shapes, (shapes) => {
        arrayMoveMutable(shapes, sourceIndex, targetIndex, count);
      });
      dispatch("calculateCys");
    },
    async setSelection({ commit, dispatch, state }, data) {
      commit("SET_SELECTION", data);
    },
    async setArrowSelection({ commit, dispatch, state }, connection) {
      const xArray = connection.attributes.reduce((prev, item) => [...prev, item[0]], []);
      const yArray = connection.attributes.reduce((prev, item) => [...prev, item[1]], []);
      const minPoint = [Math.min(...xArray), Math.min(...yArray)];
      const maxPoint = [Math.max(...xArray), Math.max(...yArray)];

      const data = {
        minPoint,
        maxPoint,
        id: connection.id,
        type: SelectionType.Arrow,
      };
      dispatch("setScope", HotkeyScopes.ProcessFlow, { root: true });

      commit("SET_SELECTION", data);
    },
    resetSelection({ commit, dispatch, state }) {
      const data = {
        minPoint: null,
        maxPoint: null,
        id: null,
        type: null,
      };
      dispatch("setScope", HotkeyScopes.ProcessDesigner, { root: true });
      commit("SET_SELECTION", data);
    },
    async deleteConnection({ commit, dispatch, state }, id) {
      const arrow = { ...state.connections[id] };
      commit("REMOVE_CONNECTION", id);
      commit("REMOVE_CONNECTION_FROM_SHAPE", {
        shapeId: arrow.startShapeId,
        connectionId: id,
      });
      commit("REMOVE_CONNECTION_FROM_SHAPE", {
        shapeId: arrow.endShapeId,
        connectionId: id,
      });
      dispatch("resetSelection");
      await dispatch("persist");
    },
    deleteClick({ commit, dispatch, state }) {
      if (state.selection.type === SelectionType.Arrow && state.selection.id) {
        dispatch("deleteConnection", state.selection.id);
      }
    },
    setArrowDraggingId({ commit, dispatch, state }, id) {
      state.arrowDraggingId = id;
    },
    setCluePointsId({ commit, dispatch, state }, id) {
      state.cluePointsId = id;
    },
    resetCluePointsId({ commit, dispatch, state }) {
      state.cluePointsId = null;
    },
    async toggleVerticalLines({ commit, dispatch, state }) {
      state.showVerticalLines = !state.showVerticalLines;
      await dispatch("persist");
    },

    calculateCys({ dispatch, state, rootGetters }) {
      // Use Immer to modify both shapeMap and connections directly
      state.shapeMap = produce(state.shapeMap, (shapeMapDraft) => {
        produce(state.connections, (connDraft) => {
          let sum = 0;

          state.shapes.forEach((shapeId) => {
            const shape = shapeMapDraft[shapeId];
            const activityId = shapeId.replace("activity_", "");
            if (!rootGetters["views/rowVisibility"](activityId)) return;

            const height = rootGetters["views/rawHeight"](activityId);
            const cy = sum + height / 2;

            // Update outgoing connections
            shape.outgoingConnections?.forEach((outgoing) => {
              const point1Diff = shape.attributes.cy - connDraft[outgoing].attributes[0][1];
              const point2Diff = shape.attributes.cy - connDraft[outgoing].attributes[1][1];

              connDraft[outgoing].attributes[0][1] = cy - point1Diff;
              connDraft[outgoing].attributes[1][1] = cy - point2Diff;
            });

            // Update incoming connections
            shape.incommingConnections?.forEach((incomming) => {
              const point3Diff = shape.attributes.cy - connDraft[incomming].attributes[2][1];
              const point4Diff = shape.attributes.cy - connDraft[incomming].attributes[3][1];

              connDraft[incomming].attributes[2][1] = cy - point3Diff;
              connDraft[incomming].attributes[3][1] = cy - point4Diff;
            });

            shape.attributes.cy = cy; // Update shape's cy position
            sum += height; // Update the sum of heights
          });
        });
      });

      // Directly modify state.connections without reassignment
      state.connections = produce(state.connections, (connDraft) => {
        // The modifications to connDraft are handled within the inner `produce`
      });
    },
    autoLayout({ dispatch, state }) {
      const newShapes = [...state.shapes];
      const newShapeMap = { ...state.shapeMap };
      const newConnections = { ...state.connections };

      sortConnections(newShapes, newShapeMap, newConnections);
      calculateLayout(newShapes, newShapeMap, newConnections, newShapeMap[newShapes[0]]);

      state.shapes = newShapes;
      state.shapeMap = newShapeMap;
      state.connections = newConnections;

      dispatch("resetSelection");
      dispatch("persist");
    },
    setFlowContextMenu({ state }, value) {
      state.flowContextMenu = value;
    },
    async resetState({ state }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    maxCx: (state) => {
      const shapeMaxCx = Math.max(...Object.values(state.shapeMap).map((shape) => shape.attributes.cx));
      const ConnectionsMaxCx = Math.max(
        ...Object.values(state.connections).flatMap((obj) => obj.attributes.flatMap((attrArray) => attrArray[0]))
      );
      return Math.max(shapeMaxCx, ConnectionsMaxCx);
    },
  },
};

function calculateLayout(shapes, shapeMap, connections, shape, startPoint = 0, traversedShapes = {}) {
  const cx = (startPoint + 1) * SPACE_BETWEEN_VLINES;

  if (traversedShapes[shape.id]) return traversedShapes[shape.id]++;
  else traversedShapes[shape.id] = 1;

  shape.attributes.cx = cx;

  let nextCx = startPoint;
  for (let i = 0; i < shape.outgoingConnections.length; i++) {
    const connectionId = shape.outgoingConnections[i];
    const connection = connections[connectionId];
    const nextShape = shapeMap[connection.endShapeId];

    if (shape.attributes.cy > nextShape.attributes.cy) {
      recalculateConnection(shapeMap, connections, connectionId, 1);
      continue;
    }
    calculateLayout(shapes, shapeMap, connections, nextShape, nextCx, traversedShapes);
    recalculateConnection(shapeMap, connections, connectionId, traversedShapes[nextShape.id] - 1);
    if (traversedShapes[nextShape.id] < 2) nextCx += maxChildCount(shapes, shapeMap, connections, nextShape);
  }
}

// in the shape subtree, what is the highest count of children a shape has
function maxChildCount(shapes, shapeMap, connections, shape, traversedShapes = {}) {
  let sum = 0;

  if (traversedShapes[shape.id]) return 0;
  traversedShapes[shape.id] = true;

  for (let i = 0; i < shape.outgoingConnections.length; i++) {
    const connectionId = shape.outgoingConnections[i];
    const connection = connections[connectionId];

    const nextShape = shapeMap[connection.endShapeId];
    sum += maxChildCount(shapes, shapeMap, connections, nextShape, traversedShapes);
  }
  return sum || 1;
}

// Draw new connection between updated shapes
function recalculateConnection(shapeMap, connections, connectionId, offset) {
  let connection = connections[connectionId];

  const startShape = shapeMap[connection.startShapeId];
  const endShape = shapeMap[connection.endShapeId];

  connections[connectionId] = {
    ...connection,
    attributes: getConnection(startShape, endShape, offset),
  };
}

function sortConnections(shapes, shapeMap, connections) {
  for (let i = 0; i < shapes.length; i++) {
    const shape = shapeMap[shapes[i]];
    shape.outgoingConnections = shape.outgoingConnections.sort((firstConnectionId, secondConnectionId) => {
      const firstConnection = connections[firstConnectionId];
      const secondConnection = connections[secondConnectionId];

      const firstShape = shapeMap[firstConnection.endShapeId];
      const secondShape = shapeMap[secondConnection.endShapeId];

      return firstShape.attributes.cy < secondShape.attributes.cy ? -1 : 1;
    });
  }
}
